import { X, Search, Building2 } from 'lucide-react';
import { useState } from 'react';

import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Input } from '@/components/ui/input';
import { ScrollArea } from '@/components/ui/scroll-area';
import { cn } from '@/lib/utils';

type Props = {
  suppliers: { label: string; value: number }[];
  selectedSuppliers: number[];
  setSelectedSuppliers: React.Dispatch<React.SetStateAction<number[]>>;
};

const SupplierSelection = ({
  suppliers,
  selectedSuppliers,
  setSelectedSuppliers,
}: Props) => {
  const [searchText, setSearchText] = useState('');

  const toggleSelection = (id: number) => {
    setSelectedSuppliers((prev) =>
      prev.includes(id) ? prev.filter((item) => item !== id) : [...prev, id],
    );
  };

  // تصفية العناصر بناءً على نص البحث
  const filteredItems = suppliers.filter(({ label }) => {
    const search = searchText.trim().toLowerCase();
    return label.toLowerCase().includes(search);
  });

  return (
    <div className="space-y-3">
      {/* Search Box */}
      <div className="relative">
        <Search className="absolute left-3 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-slate-400" />
        <Input
          type="text"
          placeholder="ابحث عن مورد..."
          value={searchText}
          onChange={(e) => setSearchText(e.target.value)}
          className="w-full pl-10 pr-3 h-9 text-sm bg-slate-50 border-slate-200 focus:bg-white dark:bg-slate-800 dark:border-slate-700"
        />
      </div>

      {/* Actions Bar */}
      <div className="flex items-center justify-between">
        <span className="text-xs text-slate-500 dark:text-slate-400">
          {filteredItems.length} من {suppliers.length} مورد
        </span>
        {selectedSuppliers.length > 0 && (
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setSelectedSuppliers([])}
            className="h-6 gap-1 px-2 text-xs text-slate-500 hover:text-red-600 dark:text-slate-400 dark:hover:text-red-400"
          >
            <X className="h-3 w-3" />
            مسح ({selectedSuppliers.length})
          </Button>
        )}
      </div>

      {/* Suppliers List */}
      <ScrollArea className="h-48">
        <div className="space-y-1 pr-3">
          {filteredItems.length > 0 ? (
            filteredItems.map(({ label, value }) => {
              const isSelected = selectedSuppliers.includes(value);
              return (
                <div
                  key={value}
                  className={cn(
                    "group flex items-center gap-3 rounded-md p-2.5 transition-colors cursor-pointer w-full",
                    isSelected 
                      ? "bg-blue-50 border border-blue-200 dark:bg-blue-950/30 dark:border-blue-800/50"
                      : "hover:bg-slate-50 dark:hover:bg-slate-800/50"
                  )}
                  onClick={() => toggleSelection(value)}
                >
                  <Checkbox
                    checked={isSelected}
                    onCheckedChange={() => toggleSelection(value)}
                    className="data-[state=checked]:bg-blue-600 data-[state=checked]:border-blue-600"
                  />
                  <div className="flex items-center gap-2 flex-1 min-w-0 max-w-full">
                    <Building2 className={cn(
                      "h-4 w-4 flex-shrink-0",
                      isSelected ? "text-blue-600 dark:text-blue-400" : "text-slate-400"
                    )} />
                    <span className={cn(
                      "text-sm truncate",
                      isSelected ? "font-medium text-blue-900 dark:text-blue-100" : "text-slate-700 dark:text-slate-300"
                    )}>
                      {label}
                    </span>
                  </div>
                  {isSelected && (
                    <Badge variant="secondary" className="h-5 px-2 text-xs">
                      محدد
                    </Badge>
                  )}
                </div>
              );
            })
          ) : (
            <div className="flex flex-col items-center justify-center py-6 text-center w-full">
              <Building2 className="h-8 w-8 text-slate-300 dark:text-slate-600 mb-2" />
              <p className="text-sm text-slate-500 dark:text-slate-400 mb-1">
                لا توجد نتائج
              </p>
              <p className="text-xs text-slate-400 dark:text-slate-500">
                جرب تغيير مصطلح البحث
              </p>
            </div>
          )}
        </div>
      </ScrollArea>
    </div>
  );
};

export default SupplierSelection;
